import { EmptyState, Skeleton } from '@estac1/ui';
import type { UseMutationResult } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { ClientCard } from './client-card.js';
import type { ModuleToggleMutation, ToggleableModuleKey } from './lot-module-toggles.js';
import type { Client, Lot, PricingSettings } from './types.js';

interface ClientListProps {
  clientsLoading: boolean;
  clientsError: boolean;
  filtered: Client[];
  search: string;
  statusFilter: string;
  pricing: PricingSettings | undefined;
  onExtend: (lot: Lot) => void;
  onArchive: (lot: Lot) => void;
  onUnarchive: (lot: Lot) => void;
  onResetCheckout: (lot: Lot) => void;
  onToggleModule: (lot: Lot, key: ToggleableModuleKey, enabled: boolean) => void;
  unarchive: UseMutationResult<unknown, unknown, string, unknown>;
  toggleModule: ModuleToggleMutation;
  resetCheckout: UseMutationResult<unknown, unknown, string, unknown>;
}

export const ClientList = ({
  clientsLoading,
  clientsError,
  filtered,
  search,
  statusFilter,
  pricing,
  onExtend,
  onArchive,
  onUnarchive,
  onResetCheckout,
  onToggleModule,
  unarchive,
  toggleModule,
  resetCheckout,
}: ClientListProps) => {
  if (clientsLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (clientsError) {
    return (
      <EmptyState
        icon={<Building2 className="h-5 w-5" />}
        title="Não foi possível carregar"
        description="Verifique sua conexão e tente novamente em instantes."
      />
    );
  }

  if (filtered.length === 0) {
    return (
      <EmptyState
        icon={<Building2 className="h-5 w-5" />}
        title={search || statusFilter !== 'all' ? 'Nenhum resultado' : 'Nenhum cliente ainda'}
        description={
          search || statusFilter !== 'all'
            ? 'Ajuste o filtro acima.'
            : 'Quando alguém criar conta, vai aparecer aqui.'
        }
      />
    );
  }

  return (
    <ul className="space-y-3">
      {filtered.map((client) => (
        <li key={client.id}>
          <ClientCard
            client={client}
            pricing={pricing}
            onExtend={onExtend}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
            onResetCheckout={onResetCheckout}
            onToggleModule={onToggleModule}
            unarchive={unarchive}
            toggleModule={toggleModule}
            resetCheckout={resetCheckout}
          />
        </li>
      ))}
    </ul>
  );
};
